interface GekkoMessage {
  welcome: string;
  followUp: string[];
}

interface GekkoResponse {
  content: string;
  followUp?: string[];
  action?: string;
}

export function getGekkoMessage(department: string): GekkoMessage {
  const messages: Record<string, GekkoMessage> = {
    'sales': {
      welcome: "Welcome to the Sales Floor! I'm Senka (戦火), your Sales Gekko. I'll help you manage customer relationships, close more deals, and build a winning sales strategy. What would you like to focus on today?",
      followUp: [
        "Show my pipeline",
        "Add new prospect",
        "View active campaigns"
      ]
    },
    'command': {
      welcome: "Welcome to Gecko HQ! I'm Kari, your Personal Assistant Gekko. I'll help you get started with your business journey. What would you like to know?",
      followUp: [
        "Let's set up my company",
        "Tell me more about Gecko HQ",
        "I need help"
      ]
    },
    'workshop': {
      welcome: "Welcome to the Workshop! I'm Seisaku (制作), your Production Floor Manager. I'll help you turn your ideas into reality through methodical project execution. How can I assist you today?",
      followUp: [
        "Create a new project",
        "Show my active tasks",
        "Explain the project workflow"
      ]
    },
    'development': {
      welcome: "Welcome to the Development Floor! I'm Kaizen, your development specialist. I'll help you build and refine your business vision and brand. What would you like to work on?",
      followUp: [
        "Let's work on business vision",
        "Help with branding",
        "Review development progress"
      ]
    },
    'operations': {
      welcome: "Welcome to Operations! I'm Ninja, your operations specialist. I'll help you manage and optimize your business processes. What would you like to focus on?",
      followUp: [
        "Show company profile",
        "Manage tasks",
        "Review operations"
      ]
    }
  };

  return messages[department] || messages['command'];
}

export function getDepartmentResponse(department: string, message: string): GekkoResponse {
  // Common responses
  if (message.toLowerCase().includes("let's set up my company") || 
      message.toLowerCase().includes("setup my company")) {
    return {
      content: "I'll help you set up your company profile. Let's gather some basic information to get started.",
      action: 'SHOW_COMPANY_FORM'
    };
  }
  
  if (message.toLowerCase().includes("skip setup") || 
      message.toLowerCase() === "skip for now") {
    return {
      content: "No problem! You can always set up your company profile later. What would you like to do now?",
      followUp: [
        "Tell me about Gekko HQ",
        "Show my dashboard",
        "What can you help with?"
      ]
    };
  }

  // Department-specific responses
  switch (department) {
    case 'command':
      if (message.toLowerCase().includes('tell me more')) {
        return {
          content: "Gecko HQ is your AI-powered business command center. You have a team of specialized AI assistants (we call them Gekkos) to help with different aspects of your business:\n\n• Kari (that's me!) - Your personal assistant and guide\n• Kaizen - Development specialist focused on vision and branding\n• Ninja - Operations expert for smooth business processes",
          followUp: [
            "Let's set up my company",
            "Meet the Development Gekko",
            "Meet the Operations Gekko",
            "Skip setup for now"
          ]
        };
      }
      return {
        content: "I'm here to help! What would you like to know?",
        followUp: [
          "Let's set up my company",
          "Tell me more about Gecko HQ",
          "I need help",
          "Skip setup for now"
        ]
      };
    
    case 'sales':
      if (message.toLowerCase().includes('show my pipeline') || message.toLowerCase().includes('battle plan')) {
        return {
          content: "Let's take a look at your sales pipeline. Currently, you don't have any active deals. Would you like to add your first prospect to get started?",
          followUp: [
            "Add new prospect",
            "Set up pipeline stages",
            "Show me how it works"
          ]
        };
      }
      
      if (message.toLowerCase().includes('add new prospect')) {
        return {
          content: "Great! Let's add a new prospect to your pipeline. You can either create a new contact or convert an existing contact to a prospect. What would you like to do?",
          followUp: [
            "Create new contact",
            "Convert existing contact",
            "Tell me more about prospects"
          ]
        };
      }
      
      if (message.toLowerCase().includes('view active campaigns')) {
        return {
          content: "You don't have any active sales campaigns yet. Would you like to create your first campaign to start generating leads?",
          followUp: [
            "Create new campaign",
            "Show campaign templates",
            "Tell me about campaign types"
          ]
        };
      }
      
      return {
        content: "I'm Senka, your Sales Gekko. I'll help you manage customer relationships, track deals, and close more sales. What would you like to focus on today?",
        followUp: [
          "Show my pipeline",
          "Add new prospect",
          "View active campaigns",
          "Sales performance metrics"
        ]
      };
    
    case 'workshop':
      if (message.toLowerCase().includes('create a new project')) {
        return {
          content: "Let's create a new project! I'll help you set it up step by step. First, what's the name of your project?",
          followUp: [
            "Website Redesign",
            "Marketing Campaign",
            "Product Launch",
            "Custom Project"
          ]
        };
      }
      
      if (message.toLowerCase().includes('show my active tasks')) {
        return {
          content: "Here's a summary of your active tasks. You currently have 8 tasks in progress and 3 that need your review. Would you like to see them organized by project or by due date?",
          followUp: [
            "Show by project",
            "Show by due date",
            "Show high priority only"
          ]
        };
      }
      
      if (message.toLowerCase().includes('explain the project workflow')) {
        return {
          content: "Our project workflow follows a structured approach to ensure quality and efficiency:\n\n1. **Vision** - Define the project's purpose and goals\n2. **Ideas** - Brainstorm concepts that align with the vision\n3. **Sub-Projects** - Break down into manageable workstreams\n4. **Features** - Define specific deliverables\n5. **Tasks** - Create actionable steps with clear ownership\n\nEach stage builds on the previous one, creating a clear path from concept to completion.",
          followUp: [
            "Create a new project",
            "Show workflow templates",
            "Customize workflow"
          ]
        };
      }
      
      return {
        content: "I'm Seisaku, your Production Floor Manager. I can help you manage projects, tasks, and workflows with methodical precision. What would you like to focus on today?",
        followUp: [
          "Create a new project",
          "Show my active tasks",
          "Explain the project workflow",
          "Optimize my current workflow"
        ]
      };
    
    case 'development':
      return {
        content: "Let's work on your business development.",
        followUp: [
          "Define business vision",
          "Work on branding",
          "Review progress"
        ]
      };
    
    case 'operations':
      return {
        content: "I'll help you optimize your operations.",
        followUp: [
          "Review company profile",
          "Manage tasks",
          "Check settings"
        ]
      };
    
    default:
      return {
        content: "I'm here to help. What would you like to focus on?",
        followUp: [
          "Show overview",
          "Review tasks",
          "Check progress"
        ]
      };
  }
}